import { WebPlugin } from '@capacitor/core';
import { AppleSocialLogin } from './apple-provider';
import { inferUserCancelledError } from './errors';
import { FacebookSocialLogin } from './facebook-provider';
import { GoogleSocialLogin } from './google-provider';
import { OAuth2SocialLogin } from './oauth2-provider';
import { TwitterSocialLogin } from './twitter-provider';
export class SocialLoginWeb extends WebPlugin {
    constructor() {
        super();
        this.googleProvider = new GoogleSocialLogin();
        this.appleProvider = new AppleSocialLogin();
        this.facebookProvider = new FacebookSocialLogin();
        this.twitterProvider = new TwitterSocialLogin();
        this.oauth2Provider = new OAuth2SocialLogin();
        // Auto-finish OAuth redirects only when running inside a popup window.
        // For redirect-based flows (full page navigation), the app should call `handleRedirectCallback()` explicitly.
        const hasPending = !!localStorage.getItem(SocialLoginWeb.OAUTH_STATE_KEY);
        const isPopup = !!window.opener || SocialLoginWeb.POPUP_WINDOW_NAMES.has(window.name);
        if (hasPending && isPopup) {
            this.finishOAuthRedirectInPopup().catch((error) => {
                console.error('Failed to finish OAuth redirect', error);
                try {
                    window.close();
                }
                catch (_a) {
                    // ignore
                }
            });
        }
    }
    async parseRedirectResult() {
        var _a;
        const url = new URL(window.location.href);
        const stateRaw = localStorage.getItem(SocialLoginWeb.OAUTH_STATE_KEY);
        let provider = null;
        let state;
        let nonce;
        if (stateRaw) {
            try {
                const parsed = JSON.parse(stateRaw);
                provider = (_a = parsed.provider) !== null && _a !== void 0 ? _a : null;
                state = parsed.state;
                nonce = parsed.nonce;
            }
            catch (_b) {
                provider = stateRaw === 'true' ? 'google' : null;
            }
        }
        let result = null;
        switch (provider) {
            case 'twitter':
                result = await this.twitterProvider.handleOAuthRedirect(url, state);
                break;
            case 'oauth2':
                result = await this.oauth2Provider.handleOAuthRedirect(url, state);
                break;
            case 'google':
            default:
                result = this.googleProvider.handleOAuthRedirect(url);
                break;
        }
        return { provider, state, nonce, result };
    }
    async finishOAuthRedirectInPopup() {
        var _a;
        const parsed = await this.parseRedirectResult();
        const result = parsed.result;
        if (!result)
            return;
        // Build the message to send
        let message;
        if ('error' in result) {
            const resolvedProvider = (_a = parsed.provider) !== null && _a !== void 0 ? _a : null;
            const error = inferUserCancelledError(result.error);
            message = Object.assign({ type: 'oauth-error', provider: resolvedProvider, error: error.message }, (error.code ? { code: error.code } : null));
        }
        else {
            message = Object.assign({ type: 'oauth-response', provider: result.provider }, result.result);
        }
        // Try postMessage first (works when window.opener is accessible)
        try {
            if (window.opener) {
                window.opener.postMessage(message, window.location.origin);
            }
        }
        catch (_b) {
            // Cross-origin error - window.opener may not be accessible
            console.log('postMessage to opener failed, using BroadcastChannel');
        }
        // Also use BroadcastChannel as a fallback (works across same-origin windows
        // even when window.opener is not accessible due to cross-origin navigation)
        try {
            // Determine the channel name based on provider and state/nonce
            let channelName = null;
            if (parsed.provider === 'oauth2' && parsed.state) {
                channelName = `oauth2_${parsed.state}`;
            }
            else if (parsed.provider === 'twitter' && parsed.state) {
                channelName = `twitter_oauth_${parsed.state}`;
            }
            else if (parsed.provider === 'google' && parsed.nonce) {
                channelName = `google_oauth_${parsed.nonce}`;
            }
            if (channelName) {
                const channel = new BroadcastChannel(channelName);
                channel.postMessage(message);
                channel.close();
            }
        }
        catch (_c) {
            // BroadcastChannel not supported or other error
            console.log('BroadcastChannel not available');
        }
        window.close();
    }
    async initialize(options) {
        var _a, _b, _c, _d;
        const initPromises = [];
        if ((_a = options.google) === null || _a === void 0 ? void 0 : _a.webClientId) {
            initPromises.push(this.googleProvider.initialize(options.google.webClientId, options.google.mode, options.google.hostedDomain, options.google.redirectUrl));
        }
        if ((_b = options.apple) === null || _b === void 0 ? void 0 : _b.clientId) {
            initPromises.push(this.appleProvider.initialize(options.apple.clientId, options.apple.redirectUrl, options.apple.useProperTokenExchange));
        }
        if ((_c = options.facebook) === null || _c === void 0 ? void 0 : _c.appId) {
            initPromises.push(this.facebookProvider.initialize(options.facebook.appId, options.facebook.locale));
        }
        if ((_d = options.twitter) === null || _d === void 0 ? void 0 : _d.clientId) {
            initPromises.push(this.twitterProvider.initialize(options.twitter.clientId, options.twitter.redirectUrl, options.twitter.defaultScopes, options.twitter.forceLogin, options.twitter.audience));
        }
        if (options.oauth2 && Object.keys(options.oauth2).length > 0) {
            initPromises.push(this.oauth2Provider.initializeProviders(options.oauth2));
        }
        await Promise.all(initPromises);
    }
    async login(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.login(options.options);
            case 'apple':
                return this.appleProvider.login(options.options);
            case 'facebook':
                return this.facebookProvider.login(options.options);
            case 'twitter':
                return this.twitterProvider.login(options.options);
            case 'oauth2':
                return this.oauth2Provider.login(options.options);
            default:
                throw new Error(`Login for ${options.provider} is not implemented on web`);
        }
    }
    async logout(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.logout();
            case 'apple':
                return this.appleProvider.logout();
            case 'facebook':
                return this.facebookProvider.logout();
            case 'twitter':
                return this.twitterProvider.logout();
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 logout');
                }
                return this.oauth2Provider.logout(options.providerId);
            default:
                throw new Error(`Logout for ${options.provider} is not implemented`);
        }
    }
    async isLoggedIn(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.isLoggedIn();
            case 'apple':
                return this.appleProvider.isLoggedIn();
            case 'facebook':
                return this.facebookProvider.isLoggedIn();
            case 'twitter':
                return this.twitterProvider.isLoggedIn();
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 isLoggedIn');
                }
                return this.oauth2Provider.isLoggedIn(options.providerId);
            default:
                throw new Error(`isLoggedIn for ${options.provider} is not implemented`);
        }
    }
    async getAuthorizationCode(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.getAuthorizationCode();
            case 'apple':
                return this.appleProvider.getAuthorizationCode();
            case 'facebook':
                return this.facebookProvider.getAuthorizationCode();
            case 'twitter':
                return this.twitterProvider.getAuthorizationCode();
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 getAuthorizationCode');
                }
                return this.oauth2Provider.getAuthorizationCode(options.providerId);
            default:
                throw new Error(`getAuthorizationCode for ${options.provider} is not implemented`);
        }
    }
    async refresh(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.refresh();
            case 'apple':
                return this.appleProvider.refresh();
            case 'facebook':
                return this.facebookProvider.refresh(options.options);
            case 'twitter':
                return this.twitterProvider.refresh();
            case 'oauth2': {
                const oauth2Options = options.options;
                if (!(oauth2Options === null || oauth2Options === void 0 ? void 0 : oauth2Options.providerId)) {
                    throw new Error('providerId is required for oauth2 refresh');
                }
                return this.oauth2Provider.refresh(oauth2Options.providerId);
            }
            default:
                throw new Error(`Refresh for ${options.provider} is not implemented`);
        }
    }
    async providerSpecificCall(options) {
        throw new Error(`Provider specific call for ${options.call} is not implemented`);
    }
    async refreshToken(options) {
        if (options.provider !== 'oauth2') {
            throw new Error('refreshToken is only implemented for oauth2 on web');
        }
        return this.oauth2Provider.refreshToken(options.providerId, options.refreshToken, options.additionalParameters);
    }
    async handleRedirectCallback() {
        const parsed = await this.parseRedirectResult();
        const result = parsed.result;
        if (!result)
            return null;
        if ('error' in result) {
            throw inferUserCancelledError(result.error);
        }
        return result;
    }
    async decodeIdToken(options) {
        var _a;
        const token = (_a = options === null || options === void 0 ? void 0 : options.idToken) !== null && _a !== void 0 ? _a : options === null || options === void 0 ? void 0 : options.token;
        if (!token) {
            throw new Error('idToken (or token) is required');
        }
        const claims = this.oauth2Provider.decodeIdToken(token);
        return { claims };
    }
    async getAccessTokenExpirationDate(options) {
        if (typeof (options === null || options === void 0 ? void 0 : options.accessTokenExpirationDate) !== 'number') {
            throw new Error('accessTokenExpirationDate is required');
        }
        return { date: new Date(options.accessTokenExpirationDate).toISOString() };
    }
    async isAccessTokenAvailable(options) {
        var _a;
        const token = (_a = options === null || options === void 0 ? void 0 : options.accessToken) !== null && _a !== void 0 ? _a : null;
        return { isAvailable: typeof token === 'string' && token.length > 0 };
    }
    async isAccessTokenExpired(options) {
        if (typeof (options === null || options === void 0 ? void 0 : options.accessTokenExpirationDate) !== 'number') {
            throw new Error('accessTokenExpirationDate is required');
        }
        return { isExpired: options.accessTokenExpirationDate <= Date.now() };
    }
    async isRefreshTokenAvailable(options) {
        var _a;
        const token = (_a = options === null || options === void 0 ? void 0 : options.refreshToken) !== null && _a !== void 0 ? _a : null;
        return { isAvailable: typeof token === 'string' && token.length > 0 };
    }
    async getPluginVersion() {
        return { version: 'web' };
    }
    async openSecureWindow(options) {
        const w = 600;
        const h = 550;
        const settings = [
            ['width', w],
            ['height', h],
            ['left', screen.width / 2 - w / 2],
            ['top', screen.height / 2 - h / 2],
        ]
            .map((x) => x.join('='))
            .join(',');
        const popup = window.open(options.authEndpoint, 'Authorization', settings);
        if (typeof popup.focus === 'function') {
            popup.focus();
        }
        return new Promise((resolve, reject) => {
            const bc = new BroadcastChannel(options.broadcastChannelName || 'oauth-channel');
            bc.addEventListener('message', (event) => {
                if (event.data.startsWith(options.redirectUri)) {
                    bc.close();
                    resolve({ redirectedUri: event.data });
                }
                else {
                    bc.close();
                    reject(new Error('Redirect URI does not match, expected ' + options.redirectUri + ' but got ' + event.data));
                }
            });
            setTimeout(() => {
                bc.close();
                reject(new Error('The sign-in flow timed out'));
            }, 5 * 60000);
        });
    }
}
SocialLoginWeb.OAUTH_STATE_KEY = 'social_login_oauth_pending';
SocialLoginWeb.POPUP_WINDOW_NAMES = new Set(['OAuth2Login', 'XLogin', 'Google Sign In', 'Authorization']);
//# sourceMappingURL=web.js.map